import {
  Box,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  keyframes,
  List,
  Stack,
  TextField,
  Typography,
  Button,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { memo, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import DrawerHeader from "./DrawerHeader";
import {
  Send,
  Close,
  HistoryOutlined,
  Edit,
  Delete,
  ErrorOutline,
  Stop,
  Lightbulb,
  Add,
  Tune,
  ContentCopy,
  Check,
} from "@mui/icons-material";
import useAutoScroll from "hooks/useAutoScroll";
import ChatBubble from "./ChatBubble";
import { AiMessage, ChatSession } from "hooks/useLLM";
import useResize from "hooks/useResize";
import { UtilityContext } from "contexts";
import MarkdownViewer from "./MarkdownViewer";
import ModelProviderMenu, {
  ModelProvider,
} from "./ModelProviderMenu";
import { HEADER_HEIGHT } from "common/theme";
import { getTranslation } from "common";
import { useSelectedOrganization, useTranslations, useUser } from "hooks";
import PromptSuggest from "./PromptSuggest";
import promptSuggestions from "./promptSuggestion.json";
import { useLocation, useNavigate } from "react-router-dom";
import { LOADING_TIPS } from "./tips";
import FlowentiButton from "components/flowenti-ui-kit/FlowentiButton";
import dayjs from "dayjs";
import "dayjs/locale/it";
import "dayjs/locale/en";
import "dayjs/locale/pt";
import "dayjs/locale/fr";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

function pauseEvent(e: any) {
  if (e.stopPropagation) e.stopPropagation();
  if (e.preventDefault) e.preventDefault();
  e.cancelBubble = true;
  e.returnValue = false;
  return false;
}

interface AiDrawerProps {
  variant?: "persistent" | "permanent" | "temporary";
  aiAnswers: AiMessage[];
  handleSubmitLLM: () => void;
  openAI: boolean;
  setOpenAI: (value: React.SetStateAction<boolean>) => void;
  LLMLoading: boolean;
  handleApplyAi?: (model: string) => Promise<void>;
  llmInputRef: React.RefObject<HTMLInputElement>;
  modelInputRef: React.RefObject<HTMLInputElement>;
  canUseAi?: boolean;
  isDashboard?: boolean;
  zIndex?: number;

  // New optional props
  stopLLM?: () => void;
  sessions?: ChatSession[];
  currentSessionId?: string;
  startNewSession?: () => void;
  loadSession?: (id: string) => void | Promise<void>;
  deleteSession?: (id: string) => void | Promise<void>;
  renameSession?: (id: string, newTitle: string) => void | Promise<void>;
  handleRetry?: (uuid: string) => void;
  maxCharLimit?: number;
  searchSessions?: (query: string) => Promise<ChatSession[]>;
  searching?: boolean;
  refreshSessions?: () => Promise<void>;

  // Provider / model selection (driven by the konami "flowenti2" hook).
  modelProvider?: ModelProvider | null;
  setModelProvider?: (next: ModelProvider) => void;
  modelName?: string;
  setModelName?: (next: string) => void;
}

const AiDrawer: React.FC<AiDrawerProps> = ({
  aiAnswers,
  handleSubmitLLM,
  openAI,
  setOpenAI,
  handleApplyAi,
  LLMLoading,
  llmInputRef,
  modelInputRef,
  variant,
  canUseAi = true,
  isDashboard,
  zIndex,
  stopLLM,
  sessions = [],
  currentSessionId,
  startNewSession,
  loadSession,
  deleteSession,
  renameSession,
  handleRetry,
  maxCharLimit = 2000,
  searchSessions,
  searching: searchingProp = false,
  refreshSessions,
  modelProvider = null,
  setModelProvider,
  modelName = "",
  setModelName,
}) => {
  const translations = useTranslations();
  const navigate = useNavigate();
  const selectedOrganization = useSelectedOrganization();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const {
    state: { modelChoice },
  } = useContext(UtilityContext);
  const { enableResize, width } = useResize({
    minWidth: 100,
    defaultWidth: 500,
    side: "right",
    maxWidth: (window.innerWidth * 60) / 100,
  });

  // On mobile we ignore the user's resized width and force a full-screen
  // drawer (the resize handle is hidden because it would be useless on a
  // 360px viewport). 100vw keeps the drawer edge-to-edge so the chat UI
  // has room to breathe.
  const effectiveWidth = isMobile ? "100vw" : width;

  const listRef = useAutoScroll(true, []);

  // Force-scroll to the bottom whenever a new message is added (e.g.
  // the user sends a second message after scrolling up to read older
  // ones). `useAutoScroll` uses an internal `shouldAutoScroll` flag
  // that gets disabled as soon as the user scrolls up, so without
  // this nudge the new message would not bring the viewport down
  // again. We track the last message's uuid as the trigger; on change
  // we wait for the next paint to ensure the new bubble is in the
  // DOM and then scroll to the bottom with a smooth animation.
  const lastUuid = aiAnswers[aiAnswers.length - 1]?.uuid;
  useEffect(() => {
    if (!lastUuid) return;
    // Wait for the next animation frame so React has flushed the new
    // bubble into the DOM and scrollHeight reflects it.
    const raf = requestAnimationFrame(() => {
      const el = listRef.current;
      if (!el) return;
      el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    });
    return () => cancelAnimationFrame(raf);
  }, [lastUuid, listRef]);

  const user = useUser();
  const lang = user?.i18n || navigator.language.split("-")[0];

  // Character limit tracking
  const [charCount, setCharCount] = useState(0);

  // Per-message "copied to clipboard" feedback. We track the uuid
  // whose message was just copied so each bubble can flip its icon
  // to a checkmark independently.
  const [copiedUuid, setCopiedUuid] = useState<string | null>(null);
  const copyTimerRef = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (copyTimerRef.current) window.clearTimeout(copyTimerRef.current);
    },
    []
  );
  const handleCopy = (uuid: string, text: string) => {
    const fallback = () => {
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      } catch {
        /* ignore */
      }
    };
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(text).catch(fallback);
    } else {
      fallback();
    }
    setCopiedUuid(uuid);
    if (copyTimerRef.current) window.clearTimeout(copyTimerRef.current);
    copyTimerRef.current = window.setTimeout(() => {
      setCopiedUuid(null);
      copyTimerRef.current = null;
    }, 1500);
  };

  // Tab view: "chat" or "history"
  const [activeTab, setActiveTab] = useState<"chat" | "history">("chat");
  const [searchQuery, setSearchQuery] = useState("");

  // Rename dialog state
  const [renameTarget, setRenameTarget] = useState<ChatSession | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [loadingSession, setLoadingSession] = useState<string | null>(null);

  // Provider / model menu anchor (only meaningful when modelChoice
  // is true, i.e. the user triggered the konami code "flowenti2").
  const [providerMenuAnchor, setProviderMenuAnchor] =
    useState<HTMLElement | null>(null);

  const spin = keyframes`
    to { transform: rotate(360deg); }
  `;

  const pulse = keyframes`
    0% { opacity: 0.95; }
    50% { opacity: 1; }
    100% { opacity: 0.95; }
  `;

  const bounce = keyframes`
    0% { transform: translateY(0); }
    100% { transform: translateY(-4px); }
  `;

  const location = useLocation();

  const handlePromptSelect = (prompt: string) => {
    if (llmInputRef.current) {
      llmInputRef.current.value = prompt;
      setCharCount(prompt.length);
      setIsCardView(false);
    }
  };

  const [isCardView, setIsCardView] = useState(true);

  // Tips rotation setup
  const [tipIndex, setTipIndex] = useState(0);
  const activeTips = useMemo(() => {
    const key = lang.toUpperCase() as keyof typeof LOADING_TIPS;
    return LOADING_TIPS[key] ?? LOADING_TIPS.EN;
  }, [lang]);

  useEffect(() => {
    if (!LLMLoading) return;
    setTipIndex(Math.floor(Math.random() * activeTips.length));
    const interval = setInterval(() => {
      setTipIndex((prev) => (prev + 1) % activeTips.length);
    }, 8500);
    return () => clearInterval(interval);
  }, [LLMLoading, activeTips.length]);

  const currentTip = activeTips[tipIndex] || activeTips[0];

  // Search sessions (debounced, backend-driven). Empty query → show all sessions.
  const [searchResults, setSearchResults] = useState<ChatSession[]>(sessions);
  const [searchingLocal, setSearchingLocal] = useState(false);
  const searchDebounceRef = useRef<number | null>(null);
  // Cache query → results so repeated identical searches don't re-hit the network
  const searchCacheRef = useRef<Map<string, ChatSession[]>>(new Map());
  // The query currently being awaited; used to discard stale results
  const inFlightQueryRef = useRef<string | null>(null);

  useEffect(() => {
    // Reset the debounce timer on every keystroke
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    const trimmed = searchQuery.trim();
    if (!trimmed) {
      setSearchResults(sessions);
      setSearchingLocal(false);
      return;
    }
    // Serve from cache instantly if we've already searched for this exact query
    const cached = searchCacheRef.current.get(trimmed);
    if (cached) {
      setSearchResults(cached);
      setSearchingLocal(false);
      return;
    }
    searchDebounceRef.current = window.setTimeout(async () => {
      if (!searchSessions) return;
      inFlightQueryRef.current = trimmed;
      setSearchingLocal(true);
      const results = await searchSessions(trimmed);
      // Discard stale responses: a newer query may have started while we awaited
      if (inFlightQueryRef.current !== trimmed) return;
      searchCacheRef.current.set(trimmed, results);
      setSearchResults(results);
      setSearchingLocal(false);
    }, 300);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [searchQuery, sessions, searchSessions]);

  // Expose local searching for the spinner (prop wins if provided)
  const isSearching = searchingProp || searchingLocal;

  // dayjs locale for relative time
  const dayjsLocale = useMemo(() => {
    const l = (lang || "en").toLowerCase();
    if (["it", "en", "pt", "fr"].includes(l)) return l;
    return "en";
  }, [lang]);

  const formatRelativeTime = (timestamp: number): string => {
    try {
      return dayjs(timestamp).locale(dayjsLocale).fromNow();
    } catch {
      return "";
    }
  };

  const openRenameDialog = (session: ChatSession) => {
    setRenameTarget(session);
    setRenameValue(session.title || "");
  };

  const closeRenameDialog = () => {
    setRenameTarget(null);
    setRenameValue("");
  };

  const confirmRename = () => {
    if (renameTarget && renameSession) {
      renameSession(renameTarget.id, renameValue);
    }
    closeRenameDialog();
  };

  /**
   * Renders a single-line title that shows a Tooltip with the full text only
   * when the title is actually truncated (scrollWidth > clientWidth).
   * Re-measures on every title change (e.g. after a manual rename).
   */
  const TruncatedTitle: React.FC<{ title: string; isActive: boolean }> = ({
    title,
    isActive,
  }) => {
    const ref = useRef<HTMLSpanElement>(null);
    const [truncated, setTruncated] = useState(false);

    useLayoutEffect(() => {
      const el = ref.current;
      if (el) {
        setTruncated(el.scrollWidth > el.clientWidth);
      }
    }, [title]);

    return (
      <Tooltip title={title} disableHoverListener={!truncated} placement="top">
        <Typography
          ref={ref}
          variant="subtitle2"
          noWrap
          sx={{ fontWeight: isActive ? 600 : 400 }}
        >
          {title}
        </Typography>
      </Tooltip>
    );
  };

  // Common roundness for the AI panel
  const ROUND_FIELD = "20px";
  const ROUND_BUTTON = "16px";
  const ROUND_ICON = "14px";

  return (
    <Drawer
      sx={{
        width: effectiveWidth,
        flexShrink: 0,
        zIndex,
        overflowX: "hidden",
        "& .MuiDrawer-paper": {
          mt: isMobile ? 0 : HEADER_HEIGHT,
          height: isMobile ? "100%" : `calc(100% - ${HEADER_HEIGHT})`,
          width: effectiveWidth,
          boxSizing: "border-box",
          zIndex,
        },
      }}
      variant={variant}
      anchor="right"
      open={openAI}
    >
      {/* Resize handle — placed on the LEFT edge (drawer is anchored to the right).
          Hidden on mobile: with a full-width drawer the handle would block the
          scroll gesture and has nothing to grab onto. */}
      {!isMobile && (
        <Box
          id="dragger"
          onMouseDown={(e) => {
            e = e || window.event;
            pauseEvent(e);
            enableResize(e);
          }}
          sx={{
            width: "10px",
            cursor: "ew-resize",
            position: "absolute",
            top: 0,
            left: 0,
            bottom: 0,
            zIndex: 100,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "background 0.15s",
            "&:hover": {
              backgroundColor: "rgba(0,0,0,0.04)",
              "& .ai-drawer-resize-bar": {
                backgroundColor: "primary.light",
              },
            },
          }}
        >
          <Box
            className="ai-drawer-resize-bar"
            sx={{
              width: 6,
              height: 40,
              borderRadius: 2,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.5,
              backgroundColor: "rgba(0,0,0,0.12)",
              padding: "6px 0",
              transition: "background-color 0.15s",
            }}
          >
            <Box sx={{ width: 5, height: 5, borderRadius: "50%", backgroundColor: "rgba(0,0,0,0.4)" }} />
            <Box sx={{ width: 5, height: 5, borderRadius: "50%", backgroundColor: "rgba(0,0,0,0.4)" }} />
            <Box sx={{ width: 5, height: 5, borderRadius: "50%", backgroundColor: "rgba(0,0,0,0.4)" }} />
          </Box>
        </Box>
      )}

      <DrawerHeader sx={{ justifyContent: "space-between", pl: isMobile ? 2 : 4, pr: 2, py: isMobile ? 1.5 : 1.5 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title={getTranslation(translations, "ai_drawer.new_chat")}>
            <IconButton
              onClick={() => {
                if (startNewSession) startNewSession();
                setCharCount(0);
                if (llmInputRef.current) llmInputRef.current.value = "";
                setIsCardView(true);
                setActiveTab("chat");
              }}
              size="small"
              sx={{ borderRadius: ROUND_ICON }}
            >
              <Add />
            </IconButton>
          </Tooltip>

          <Tooltip title={getTranslation(translations, "settings.histories.label")}>
            <IconButton
              onClick={() => {
                const next = activeTab === "chat" ? "history" : "chat";
                setActiveTab(next);
                // Refetch the history list from the backend every time
                // the user opens the history tab, so freshly generated
                // titles (delivered via the `Title` response header
                // after the first AI response) are picked up even if
                // the user never navigated away from the page.
                if (next === "history" && refreshSessions) {
                  refreshSessions();
                }
              }}
              size="small"
              color={activeTab === "history" ? "primary" : "default"}
              sx={{ borderRadius: ROUND_ICON }}
            >
              <HistoryOutlined />
            </IconButton>
          </Tooltip>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          {modelChoice && (
            <>
              <Tooltip title="Provider & modello">
                <IconButton
                  size="small"
                  onClick={({ currentTarget }) =>
                    setProviderMenuAnchor(currentTarget)
                  }
                  color={modelProvider ? "primary" : "default"}
                  sx={{ borderRadius: ROUND_ICON }}
                >
                  <Tune fontSize="small" />
                </IconButton>
              </Tooltip>
              <ModelProviderMenu
                open={Boolean(providerMenuAnchor)}
                anchorEl={providerMenuAnchor}
                onClose={() => setProviderMenuAnchor(null)}
                provider={modelProvider}
                model={modelName}
                onApply={(next) => {
                  setModelProvider?.(next.provider);
                  setModelName?.(next.model);
                  setProviderMenuAnchor(null);
                }}
              />
            </>
          )}

          <Tooltip title={getTranslation(translations, "pm.minimap.close.lable")}>
            <IconButton
              onClick={() => setOpenAI(false)}
              aria-label={getTranslation(translations, "pm.minimap.close.lable")}
              sx={{
                borderRadius: ROUND_ICON,
                color: 'text.secondary',
                '&:hover': {
                  color: 'error.main',
                  transform: 'rotate(90deg)',
                  transition: 'all 0.3s ease-in-out',
                }
              }}
            >
              <Close />
            </IconButton>
          </Tooltip>
        </Stack>
      </DrawerHeader>
      <Divider />

      {activeTab === "history" ? (
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', flex: 1, overflow: 'auto' }}>
          <TextField
            size="small"
            fullWidth
            placeholder={getTranslation(translations, "ai_drawer.search_placeholder")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              sx: { borderRadius: ROUND_FIELD },
            }}
          />
          {isSearching && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
              <CircularProgress size={16} />
            </Box>
          )}
          {searchResults.length === 0 ? (
            <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', mt: 4 }}>
              {getTranslation(translations, "ai_drawer.no_chats_found")}
            </Typography>
          ) : (
            <List sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {searchResults.map((session) => (
                <Box
                  key={session.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 1.5,
                    borderRadius: ROUND_FIELD,
                    border: '1px solid',
                    borderColor: (theme) =>
                      session.id === currentSessionId
                        ? "primary.light"
                        : theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.16)"
                          : theme.palette.grey[200],
                    backgroundColor: (theme) =>
                      session.id === currentSessionId
                        ? theme.palette.mode === "dark"
                          ? "rgba(84, 189, 193, 0.14)"
                          : "primary.50"
                        : "background.paper",
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      backgroundColor: (theme) =>
                        theme.palette.mode === "dark"
                          ? "rgba(84, 189, 193, 0.10)"
                          : theme.palette.grey[50],
                      transform: 'translateY(-1px)',
                      boxShadow: (theme) =>
                        theme.palette.mode === "dark"
                          ? "0 2px 10px rgba(0, 0, 0, 0.35)"
                          : "0 2px 8px rgba(0,0,0,0.05)",
                    }
                  }}
                  onClick={async () => {
                    if (loadSession) {
                      setLoadingSession(session.id);
                      try {
                        await loadSession(session.id);
                      } finally {
                        setLoadingSession(null);
                      }
                    }
                    const hasMessages = (session.messages?.length ?? 0) > 0;
                    setIsCardView(!hasMessages);
                    setActiveTab("chat");
                  }}
                >
                  <Box sx={{ flex: 1, overflow: 'hidden', mr: 1 }}>
                    <TruncatedTitle
                      title={session.title}
                      isActive={session.id === currentSessionId}
                    />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: "0.7rem" }}
                    >
                      {formatRelativeTime(session.timestamp)}
                    </Typography>
                  </Box>
                  {loadingSession === session.id ? (
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                  ) : null}
                  {/* Rename (pencil) */}
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      openRenameDialog(session);
                    }}
                    aria-label={getTranslation(translations, "ai_drawer.rename_chat")}
                    sx={{
                      borderRadius: ROUND_ICON,
                      color: 'text.secondary',
                      '&:hover': { color: 'primary.main' }
                    }}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                  {/* Delete (trash) */}
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (deleteSession) deleteSession(session.id);
                    }}
                    aria-label={getTranslation(translations, "ai_drawer.delete_chat")}
                    sx={{
                      borderRadius: ROUND_ICON,
                      color: 'text.secondary',
                      '&:hover': { color: 'error.main' }
                    }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </List>
          )}
        </Box>
      ) : (
        <>
          <List
            ref={listRef}
            sx={{
              overflowX: "hidden",
              flex: 1,
              display: "flex",
              flexDirection: "column",
              py: 2,
              px: isMobile ? 2 : 4,
              overflow: "auto",
              flexShrink: 0,
              position: 'relative',
              // The global theme (see MuiCssBaseline) sets every
              // ::-webkit-scrollbar to width/height: 5px, which makes
              // the chat scrollbar so thin that the mouse cursor
              // cannot grab it. We override it here with a chunkier
              // 12px scrollbar and a thumb that's clearly visible
              // against the track. The Firefox/Edge equivalent
              // (`scrollbar-width: thin; scrollbar-color`) is also
              // bumped so non-Chromium browsers match.
              "&::-webkit-scrollbar": {
                width: 12,
                height: 12,
                backgroundColor: "transparent",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.08)"
                    : "rgba(0, 0, 0, 0.04)",
                borderRadius: 8,
                margin: 4,
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(84, 189, 193, 0.55)"
                    : "rgba(0, 0, 0, 0.32)",
                borderRadius: 8,
                border: "2px solid transparent",
                backgroundClip: "padding-box",
                "&:hover": {
                  backgroundColor: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(84, 189, 193, 0.75)"
                      : "rgba(0, 0, 0, 0.48)",
                  backgroundClip: "padding-box",
                },
                "&:active": {
                  backgroundColor: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(84, 189, 193, 0.9)"
                      : "rgba(0, 0, 0, 0.6)",
                  backgroundClip: "padding-box",
                },
              },
              scrollbarWidth: "thin",
              scrollbarColor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(84, 189, 193, 0.65) rgba(255, 255, 255, 0.08)"
                  : "rgba(0, 0, 0, 0.32) rgba(0, 0, 0, 0.04)",
            }}
          >
            {aiAnswers.length === 0 && (
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: 0.30,
                  pointerEvents: 'none',
                  userSelect: 'none',
                  position: 'absolute',
                  inset: 0,
                }}
              >
                <Box
                  component="img"
                  src={require('./flowenti-chat-mark.svg').default || require('./flowenti-chat-mark.svg')}
                  alt="Flowenti AI"
                  sx={{ width: isMobile ? 110 : 160, height: isMobile ? 110 : 160, mb: 1 }}
                />
                <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 700, letterSpacing: 1 }}>
                  Flowenti AI
                </Typography>
              </Box>
            )}
            {aiAnswers.map(
              ({ uuid, isAi, message, isLoading, hidden, parsing, isError }, i) =>
                !hidden && (
                  <React.Fragment key={uuid}>
                    {isLoading ? (
                      // Simplified in-stream loading bubble: the lightbulb
                      // + tip card now lives at the bottom of the drawer
                      // (see the lightbulb tip card block below the message
                      // list). Here we render three bouncing dots in
                      // Flowenti primary blue, matching the dots used in
                      // the "Generazione modello" parsing state, so the
                      // user has a consistent visual signal.
                      <ChatBubble isAi>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.25,
                            py: 1.5,
                            minHeight: 40,
                          }}
                        >
                          {[0, 1, 2].map((i) => (
                            <Box
                              key={i}
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                backgroundColor: "primary.main",
                                animation: `${bounce} 0.6s infinite alternate`,
                                animationDelay: `${i * 0.15}s`,
                                display: "inline-block",
                              }}
                            />
                          ))}
                        </Box>
                      </ChatBubble>
                    ) : (
                      <ChatBubble
                        isAi={isAi}
                        footer={
                          isAi && !isError && message && !parsing
                            ? (() => {
                                const isCopied = copiedUuid === uuid;
                                const label = isCopied
                                  ? getTranslation(translations, "ai_drawer.copied")
                                  : getTranslation(translations, "ai_drawer.copy");
                                return (
                                  <Tooltip title={label} placement="top" enterDelay={400}>
                                    <IconButton
                                      size="small"
                                      aria-label={label}
                                      onClick={() => handleCopy(uuid, message)}
                                      sx={{
                                        borderRadius: ROUND_ICON,
                                        color: isCopied ? "success.main" : "text.secondary",
                                        transition: "color 0.2s, background-color 0.2s",
                                        "&:hover": {
                                          color: "primary.main",
                                          backgroundColor: "action.hover",
                                        },
                                      }}
                                    >
                                      {isCopied ? <Check fontSize="small" /> : <ContentCopy fontSize="small" />}
                                    </IconButton>
                                  </Tooltip>
                                );
                              })()
                            : undefined
                        }
                      >
                        {isError ? (
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 1.5,
                              py: 1.5,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                color: "error.main",
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                fontWeight: 500,
                              }}
                            >
                              <ErrorOutline fontSize="small" />
                              {message}
                            </Typography>
                            {handleRetry && (
                              <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                onClick={() => handleRetry(uuid)}
                                sx={{
                                  alignSelf: "flex-start",
                                  borderRadius: ROUND_BUTTON,
                                  textTransform: "none",
                                  fontWeight: 600,
                                  borderColor: "error.light",
                                  "&:hover": {
                                    backgroundColor: "error.50",
                                    borderColor: "error.main",
                                  },
                                }}
                              >
                                {getTranslation(translations, "ai_drawer.retry")}
                              </Button>
                            )}
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              flexWrap: "wrap",
                            }}
                          >
                            <Box sx={{ minWidth: 0, flex: "1 1 auto" }}>
                              <MarkdownViewer
                                handleApplyAi={handleApplyAi}
                                message={message}
                                uuid={uuid}
                              />
                            </Box>
                            {!canUseAi && selectedOrganization && isAi && i === aiAnswers.length - 1 && (
                              <FlowentiButton
                                type="button"
                                size="small"
                                variant="outlined"
                                onClick={() =>
                                  navigate(
                                    `/dashboard/organizations/${selectedOrganization.id}/billing`,
                                    { state: { organizationName: selectedOrganization.name } }
                                  )
                                }
                              >
                                {getTranslation(translations, "generic.upgrade")}
                              </FlowentiButton>
                            )}
                          </Box>
                        )}
                        {Boolean(parsing) && (
                          <Stack
                            direction="row"
                            spacing={0.5}
                            alignItems="center"
                            sx={{ mt: 1, color: "primary.main" }}
                          >
                            {/* Three hopping dots that "saltellano" like
                                Gemini, staggered so they bounce one after
                                the other. Combined with the localized
                                label, the rendered text reads
                                "...Generazione modello". Dots are coloured
                                in the Flowenti primary blue. */}
                            {[0, 1, 2].map((i) => (
                              <Box
                                key={i}
                                sx={{
                                  width: 6,
                                  height: 6,
                                  borderRadius: "50%",
                                  backgroundColor: "primary.main",
                                  animation: `${bounce} 0.6s infinite alternate`,
                                  animationDelay: `${i * 0.15}s`,
                                  display: "inline-block",
                                }}
                              />
                            ))}
                            <Typography
                              variant="body2"
                              sx={{
                                fontStyle: "italic",
                                ml: 0.5,
                                color: "text.secondary",
                              }}
                            >
                              {promptSuggestions[lang.toUpperCase() as unknown as keyof typeof promptSuggestions]?.generating_model || "Generating model"}
                            </Typography>
                          </Stack>
                        )}
                      </ChatBubble>
                    )}
                  </React.Fragment>
                )
            )}
          </List>

          {/* Bottom area: prompt suggestions on an empty new chat, or the
              lightbulb tip card while a response is loading. We no longer
              show the suggestions during loading (they would compete with
              the tip card for the same space) — the tip card takes over
              the "what's happening" role. */}
          {!location.pathname.includes("/form-builder") && (
            <>
              {isCardView && !LLMLoading && (
                <PromptSuggest
                  onPromptSelect={handlePromptSelect}
                  categories={
                    isDashboard
                      ? promptSuggestions[lang.toUpperCase() as unknown as keyof typeof promptSuggestions].dashboardCategories
                      : promptSuggestions[lang.toUpperCase() as unknown as keyof typeof promptSuggestions].categories
                  }
                  suggestions={
                    isDashboard
                      ? promptSuggestions[lang.toUpperCase() as unknown as keyof typeof promptSuggestions].dashboardSuggestions
                      : promptSuggestions[lang.toUpperCase() as unknown as keyof typeof promptSuggestions].suggestions
                  }
                />
              )}
              {LLMLoading && (
                <Box
                  sx={{
                    px: 2,
                    py: 1.5,
                    mx: 2,
                    mb: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 1,
                    borderRadius: ROUND_FIELD,
                    backgroundColor: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(84, 189, 193, 0.10)"
                        : theme.palette.grey[100],
                    border: "1px solid",
                    borderColor: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.18)"
                        : theme.palette.grey[200],
                    boxShadow: (theme) =>
                      theme.palette.mode === "dark"
                        ? "0 2px 12px rgba(0, 0, 0, 0.35)"
                        : "0 2px 8px rgba(0,0,0,0.04)",
                    animation: `${pulse} 2s infinite ease-in-out`,
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Lightbulb
                      color="warning"
                      sx={{ animation: `${bounce} 0.6s infinite alternate` }}
                    />
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: "bold",
                        color: (theme) =>
                          theme.palette.mode === "dark"
                            ? theme.palette.text.primary
                            : theme.palette.text.secondary,
                        letterSpacing: 0.5,
                      }}
                    >
                      {getTranslation(translations, "ai_drawer.loading_tip_label")}
                    </Typography>
                  </Stack>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.primary",
                      textAlign: "center",
                      minHeight: "32px",
                      px: 1,
                      fontStyle: "italic",
                    }}
                  >
                    "{currentTip}"
                  </Typography>
                </Box>
              )}
            </>
          )}

          {/* Message input — docked to the bottom, with the send/stop
              button now embedded inside the text field on the right.
              The field is visually heavier than before (thicker border
              on focus, larger font, soft shadow) to give it more
              weight as the primary interaction surface of the drawer. */}
          <Box sx={{ pt: 1, pb: isMobile ? 5 : 3, px: 2 }}>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (charCount > 0 && charCount <= maxCharLimit) {
                  handleSubmitLLM();
                  setIsCardView(false);
                  setCharCount(0);
                }
              }}
            >
              <TextField
                inputRef={llmInputRef}
                size="small"
                fullWidth
                multiline
                maxRows={10}
                minRows={1}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    if (!e.shiftKey && !e.ctrlKey) {
                      e.preventDefault();
                      if (charCount > 0 && charCount <= maxCharLimit) {
                        handleSubmitLLM();
                        setIsCardView(false);
                        setCharCount(0);
                      }
                    }
                  }
                }}
                onChange={(e) => {
                  setCharCount(e.target.value.length);
                }}
                InputProps={{
                  // The send/stop button is rendered as an endAdornment
                  // so it lives inside the input field on the right,
                  // matching the modern Gemini/Claude/ChatGPT layout.
                  endAdornment:
                    LLMLoading && stopLLM ? (
                      <Tooltip
                        title={getTranslation(translations, "ai_drawer.stop")}
                        placement="top"
                      >
                        <span>
                          <IconButton
                            color="error"
                            onClick={stopLLM}
                            aria-label={getTranslation(
                              translations,
                              "ai_drawer.stop"
                            )}
                            size="small"
                            sx={{
                              // Same look as the original stop button:
                              // red square (the Stop icon) inside a soft
                              // red circle (border + hover background in
                              // the error palette). Kept compact so it
                              // fits inside the text field as an
                              // endAdornment.
                              border: "1px solid",
                              borderColor: "error.light",
                              borderRadius: ROUND_BUTTON,
                              p: 1,
                              ml: 0.5,
                              "&:hover": {
                                backgroundColor: "error.50",
                              },
                            }}
                          >
                            <Stop fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    ) : (
                      <Tooltip
                        title={getTranslation(
                          translations,
                          "generic.send.caption"
                        )}
                        placement="top"
                      >
                        <span>
                          <IconButton
                            type="submit"
                            aria-label={getTranslation(
                              translations,
                              "generic.send.caption"
                            )}
                            disabled={
                              !canUseAi ||
                              charCount === 0 ||
                              charCount > maxCharLimit
                            }
                            size="small"
                            color="primary"
                            sx={{
                              borderRadius: ROUND_ICON,
                              ml: 0.5,
                              backgroundColor:
                                !canUseAi ||
                                charCount === 0 ||
                                charCount > maxCharLimit
                                  ? "transparent"
                                  : "primary.main",
                              color:
                                !canUseAi ||
                                charCount === 0 ||
                                charCount > maxCharLimit
                                  ? "text.disabled"
                                  : "common.white",
                              transition: "all 0.2s ease",
                              "&:hover": {
                                backgroundColor:
                                  !canUseAi ||
                                  charCount === 0 ||
                                  charCount > maxCharLimit
                                    ? "transparent"
                                    : "primary.dark",
                              },
                            }}
                          >
                            <Send fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    ),
                  sx: {
                    fontSize: "15px",
                    lineHeight: "1.5",
                    py: 1.25,
                    px: 1.75,
                    pr: 1,
                    borderRadius: ROUND_FIELD,
                    backgroundColor: "background.paper",
                    transition: "all 0.2s ease",
                    maxHeight: "200px",
                    overflowY: "auto",
                  },
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    paddingTop: "10px",
                    paddingBottom: "10px",
                    borderRadius: ROUND_FIELD,
                    // Heavier default border to make the input feel
                    // more prominent than a regular TextField.
                    "& fieldset": {
                      borderWidth: "1.5px",
                      borderColor: "divider",
                    },
                    // On hover / focus, switch to a thicker, primary-
                    // colored border with a soft shadow to draw the
                    // eye and signal interactivity.
                    "&:hover fieldset": {
                      borderColor: "primary.light",
                      borderWidth: "1.5px",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "primary.main",
                      borderWidth: "2px",
                      boxShadow: "0 0 0 4px rgba(84, 189, 193, 0.12)",
                    },
                  },
                  "& .MuiInputBase-input": {
                    scrollbarWidth: "thin",
                    "&::-webkit-scrollbar": {
                      width: "8px",
                    },
                    "&::-webkit-scrollbar-track": {
                      background: "#f1f1f1",
                      borderRadius: "10px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      background: "#888",
                      borderRadius: "10px",
                      "&:hover": {
                        background: "#555",
                      },
                    },
                  },
                }}
                placeholder={getTranslation(translations, "ask_ai_placeholder")}
              />
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  textAlign: "left",
                  mt: 0.75,
                  ml: 1.5,
                  color: charCount > maxCharLimit ? "error.main" : "text.secondary",
                  fontSize: "11px",
                  fontWeight: charCount > maxCharLimit ? 700 : 400,
                  transition: "color 0.2s",
                }}
              >
                {charCount}/{maxCharLimit}
              </Typography>
            </form>
          </Box>
        </>
      )}

      {/* Rename chat dialog */}
      <Dialog
        open={Boolean(renameTarget)}
        onClose={closeRenameDialog}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: ROUND_FIELD } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          {getTranslation(translations, "ai_drawer.rename_chat_title")}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                confirmRename();
              }
            }}
            placeholder={getTranslation(translations, "ai_drawer.rename_chat_placeholder")}
            inputProps={{ maxLength: 80 }}
            InputProps={{ sx: { borderRadius: ROUND_FIELD } }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={closeRenameDialog}
            sx={{ borderRadius: ROUND_BUTTON, textTransform: "none" }}
          >
            {getTranslation(translations, "generic.button.cancel")}
          </Button>
          <Button
            onClick={confirmRename}
            variant="contained"
            disabled={!renameValue.trim()}
            sx={{ borderRadius: ROUND_BUTTON, textTransform: "none" }}
          >
            {getTranslation(translations, "generic.button.savechanges")}
          </Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  );
};

export default memo(AiDrawer, (prev, next) =>
  prev.variant === next.variant &&
  prev.aiAnswers === next.aiAnswers &&
  prev.openAI === next.openAI &&
  prev.LLMLoading === next.LLMLoading &&
  prev.canUseAi === next.canUseAi &&
  prev.zIndex === next.zIndex &&
  prev.sessions === next.sessions &&
  prev.currentSessionId === next.currentSessionId &&
  prev.maxCharLimit === next.maxCharLimit &&
  // Provider/model state must be tracked too: when the user picks a
  // provider/model via the konami menu, the state inside useLLM is
  // updated, and these props change. Without these checks memo would
  // skip the re-render, leaving <PromptSuggest /> and the form's
  // onSubmit pointing at a stale handleSubmitLLM closure that
  // captured the previous (null/empty) model values. The first
  // submit after picking a model would then send empty provider
  // and model in the payload; the next submit (after some other
  // state change finally re-renders the drawer) would work.
  prev.modelProvider === next.modelProvider &&
  prev.modelName === next.modelName &&
  prev.setModelProvider === next.setModelProvider &&
  prev.setModelName === next.setModelName
);
